import React, {useEffect, useState, useMemo, useCallback} from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableFooter,
  Paper,
  Checkbox,
  TextField,
  Grid,
  Button,
  Typography,
  IconButton,
} from '@material-ui/core';
import {ExpandMore, ExpandLess} from '@material-ui/icons';
import {makeStyles, withStyles, useTheme} from '@material-ui/core/styles/index';
import {Skeleton} from '@material-ui/lab';
import {APICall} from '../../api/api';
import {Alert} from '@material-ui/lab';
import usePersistentAuthStore from '../../store/persistentAuthStore';

const StyledTableCell = withStyles(theme => ({
  head: {
    background: theme.palette.graphColors.themeDarkGray,
    borderBottom: `1px solid ${theme.palette.graphColors.themeDarkGray}`,
    fontWeight: 'bold',
    padding: 8,
  },
  body: {
    borderBottom: `1px solid ${theme.palette.graphColors.themeDarkGray}`,
    padding: 10,
    verticalAlign: 'middle',
  },
}))(TableCell);

const useStyles = makeStyles(theme => ({
  TableContainer: {
    // height: '45vh',
  },
}));

const tableSkeletonRows = (rows, columns = 1) =>
  Array.from({length: rows}, (x, i) => {
    return (
      <TableRow key={i}>
        {Array.from({length: columns}, (_, j) => (
          <StyledTableCell key={j}>
            <Skeleton height={41} />
          </StyledTableCell>
        ))}
      </TableRow>
    );
  });

export function IABcategoriesTable({
  domainId,
  selectedCategories,
  setSelectedCategories,
  onBlocklistLoaded,
}) {
  const theme = useTheme();
  const classes = useStyles();
  const {auth} = usePersistentAuthStore();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [categoriesList, setCategoriesList] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpanded = id => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getChildren = parentId => {
    if (!categoriesList) return [];
    return categoriesList.filter(c => String(c.parent_id || '') === String(parentId || ''));
  };

  const expandableIds = useMemo(() => {
    if (!categoriesList) return new Set();
    const getChildrenOf = id =>
      categoriesList.filter(c => String(c.parent_id || '') === String(id || ''));
    return new Set(
      categoriesList.filter(c => getChildrenOf(c.unique_id).length > 0).map(c => c.unique_id)
    );
  }, [categoriesList]);

  const expandAll = () => {
    setExpandedIds(new Set(expandableIds));
    setPage(0);
  };
  const collapseAll = () => {
    setExpandedIds(new Set());
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const matchesSearch = useMemo(() => {
    if (!searchQuery) return () => true;
    const q = searchQuery.toLowerCase();
    return c =>
      (c.name && c.name.toLowerCase().includes(q)) ||
      (c.unique_id && String(c.unique_id).toLowerCase().includes(q));
  }, [searchQuery]);

  const visibleRows = useMemo(() => {
    if (!categoriesList || error) return [];
    const roots = categoriesList.filter(c => c.parent_id == null);
    const result = [];
    const addWithChildren = (parentId, depth, includeAll) => {
      const children = getChildren(parentId);
      for (const child of children) {
        const isMatch = matchesSearch(child);
        if (includeAll || isMatch) {
          result.push({...child, _depth: depth});
          if (expandedIds.has(child.unique_id)) {
            addWithChildren(child.unique_id, depth + 1, true);
          }
        }
      }
    };
    if (searchQuery) {
      const visit = (node, depth, parentAdded, parentExpanded) => {
        const isMatch = matchesSearch(node);
        const addThis = isMatch || (parentAdded && parentExpanded);
        if (addThis) {
          result.push({...node, _depth: depth});
        }
        const isExpanded = expandedIds.has(node.unique_id);
        for (const child of getChildren(node.unique_id)) {
          visit(child, depth + 1, addThis, isExpanded);
        }
      };
      for (const root of roots) {
        visit(root, 0, false, false);
      }
      return result;
    }
    for (const root of roots) {
      result.push({...root, _depth: 0});
      if (expandedIds.has(root.unique_id)) {
        addWithChildren(root.unique_id, 1, true);
      }
    }
    return result;
  }, [categoriesList, error, searchQuery, expandedIds, matchesSearch]);

  useEffect(() => {
    if (!domainId) {
      setCategoriesList(null);
      setSelectedCategories([]);
      return;
    }

    let cancelled = false;
    async function loadBlocklist() {
      setError(null);
      setCategoriesList(null);
      const result = await APICall({
        endpoint: 'getBlocklist',
        params: domainId,
        options: {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${auth.token}`,
            'Cache-Control': 'max-age=0, no-cache',
          },
        },
      });

      if (cancelled) return;
      if (!result || result.success === false) {
        setError(result?.error || 'Error loading blocklist');
        return;
      }

      const categories = result.categories || [];
      setCategoriesList(categories);
      setSelectedCategories(categories.filter(c => c.blocked).map(c => c.unique_id));
      if (typeof onBlocklistLoaded === 'function') {
        onBlocklistLoaded({ categories, urls: result.urls || [] });
      }
    }

    loadBlocklist();
    return () => {
      cancelled = true;
    };
  }, [domainId, onBlocklistLoaded]);

  const getAllDescendants = useCallback(
    cat => {
      if (!categoriesList || !cat) return [];
      const list = [cat];
      const collect = pid => {
        for (const c of categoriesList) {
          if (String(c.parent_id || '') === String(pid || '')) {
            list.push(c);
            collect(c.unique_id);
          }
        }
      };
      collect(cat.unique_id);
      return list;
    },
    [categoriesList]
  );

  const getSiblings = category =>
    !category?.parent_id || !categoriesList
      ? []
      : categoriesList.filter(c => String(c.parent_id || '') === String(category.parent_id || ''));

  const getAllAncestors = cat => {
    if (!categoriesList || !cat?.parent_id) return [];
    const list = [];
    for (let c = cat; c?.parent_id; ) {
      const parent = categoriesList.find(r => String(r.unique_id) === String(c.parent_id));
      if (!parent) break;
      list.push(parent);
      c = parent;
    }
    return list;
  };

  const toggleSelect = (category, isTopLevelCategory, checked) => {
    const ids = list => list.map(c => String(c.unique_id));
    const allIds = ids(getAllDescendants(category));
    const hasId = (arr, id) => arr.some(x => String(x) === String(id));

    if (checked) {
      let next = [...new Set([...selectedCategories, ...allIds])];
      if (!isTopLevelCategory) {
        for (let c = category; c?.parent_id; ) {
          const parent = categoriesList.find(r => String(r.unique_id) === String(c.parent_id));
          if (!parent) break;
          const siblings = getSiblings(c);
          if (siblings.every(s => hasId(next, s.unique_id)))
            next = [...next, String(parent.unique_id)];
          else break;
          c = parent;
        }
      }
      setSelectedCategories([...new Set(next.map(String))]);
    } else {
      const toRemove = [...allIds, ...ids(getAllAncestors(category))];
      setSelectedCategories(selectedCategories.filter(id => !hasId(toRemove, id)));
    }
  };

  if (!domainId)
    return (
      <Grid container style={{marginTop: 10}}>
        <Grid item xs={12}>
          <Typography gutterBottom color={'textSecondary'} variant={'subtitle'} display={'block'}>
            3. Select advertiser categories to block:
          </Typography>
          <Alert severity="info" style={{width: 'fit-content'}}>
            Select a domain
          </Alert>
        </Grid>
      </Grid>
    );

  if (error)
    return (
      <Alert severity="error" style={{width: '100%'}}>
        {error}
      </Alert>
    );

  return (
    <Grid container style={{marginTop: 10}}>
      <Grid item xs={6}>
        <Typography gutterBottom color={'textSecondary'} variant={'subtitle'} display={'block'}>
          3. Select advertiser categories to block:
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Paper>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
              padding: 16,
              borderBottom: `1px solid ${theme.palette.divider}`,
              background: theme.palette.action.hover,
            }}
          >
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search"
              onChange={({target: {value}}) => setSearchQuery(value)}
              style={{minWidth: 320}}
              InputProps={{style: {fontSize: 14}}}
            />
            <div style={{marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8}}>
              <Button
                variant="outlined"
                size="small"
                onClick={expandAll}
                disabled={expandableIds.size === 0}
                style={{textTransform: 'none'}}
              >
                Expand all
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={collapseAll}
                disabled={expandedIds.size === 0}
                style={{textTransform: 'none'}}
              >
                Collapse all
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                disabled={!selectedCategories.length}
                onClick={() => setSelectedCategories([])}
                style={{textTransform: 'none'}}
              >
                Unblock all ({selectedCategories.length})
              </Button>
            </div>
          </div>
          <TableContainer className={classes.TableContainer}>
            <Table>
              <TableBody>
                {!categoriesList && tableSkeletonRows(rowsPerPage, 1)}

                {categoriesList && visibleRows.length === 0 && (
                  <TableRow>
                    <StyledTableCell colSpan={1} align="center">
                      No categories found
                    </StyledTableCell>
                  </TableRow>
                )}

                {categoriesList &&
                  visibleRows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(category => {
                      const isTopLevelCategory = category.parent_id == null;
                      const isChecked = selectedCategories.includes(String(category.unique_id));
                      const hasChildren = getChildren(category.unique_id).length > 0;
                      const isExpanded = expandedIds.has(category.unique_id);
                      const depth =
                        category.depth != null ? category.depth - 1 : (category._depth ?? 0);

                      let isIndeterminate = false;
                      if (!isChecked && hasChildren) {
                        const descendants = getAllDescendants(category).slice(1);
                        isIndeterminate = descendants.some(d =>
                          selectedCategories.includes(String(d.unique_id))
                        );
                      }

                      return (
                        <TableRow
                          key={category.unique_id}
                          hover
                          onClick={() =>
                            toggleSelect(
                              category,
                              isTopLevelCategory,
                              !(isChecked || isIndeterminate)
                            )
                          }
                          style={{cursor: 'pointer'}}
                        >
                          <StyledTableCell
                            align="left"
                            style={{
                              fontFamily: 'inherit',
                              paddingLeft: 16 + depth * 44,
                              ...(depth === 0 ? {fontSize: 14} : {opacity: 0.9}),
                            }}
                          >
                            <div style={{display: 'flex', alignItems: 'center', gap: 0}}>
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: 40,
                                  flexShrink: 0,
                                }}
                                onClick={e => hasChildren && e.stopPropagation()}
                              >
                                {hasChildren ? (
                                  <IconButton
                                    size="small"
                                    onClick={e => {
                                      e.stopPropagation();
                                      toggleExpanded(category.unique_id);
                                    }}
                                    style={{
                                      padding: 4,
                                      margin: -4,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      height: 32,
                                      width: 32,
                                    }}
                                  >
                                    {isExpanded ? (
                                      <ExpandLess style={{fontSize: 24}} />
                                    ) : (
                                      <ExpandMore style={{fontSize: 24}} />
                                    )}
                                  </IconButton>
                                ) : null}
                              </span>
                              <span onClick={e => e.stopPropagation()}>
                                <Checkbox
                                  checked={isChecked || isIndeterminate}
                                  color={'primary'}
                                  onChange={({target: {checked}}) =>
                                    toggleSelect(category, isTopLevelCategory, checked)
                                  }
                                  indeterminate={isIndeterminate}
                                  style={{padding: 4, marginRight: 4}}
                                />
                              </span>
                              <span
                                style={{display: 'flex', alignItems: 'center', lineHeight: 1.4}}
                              >
                                {category.name}
                                {hasChildren && (
                                  <span
                                    style={{color: theme.palette.text.secondary, marginLeft: 4}}
                                  >
                                    ({getAllDescendants(category).length - 1})
                                  </span>
                                )}
                              </span>
                            </div>
                          </StyledTableCell>
                        </TableRow>
                      );
                    })}
              </TableBody>

              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    colSpan={1}
                    count={visibleRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: {'aria-label': 'categories per page'},
                      native: true,
                    }}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={({target}) => {
                      setRowsPerPage(parseInt(target.value));
                      setPage(0);
                    }}
                    labelRowsPerPage={'Categories per page'}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );
}
